import React, { useEffect, useState } from 'react';
import { 
  Button, Box, Container, Paper, Typography, CircularProgress, Backdrop,
  TextField, InputAdornment, Tooltip, IconButton, Stack, Chip, Card, CardContent, Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ReusableDataGrid from '../../../components/ReusableDataGrid';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import * as saleOrderService from '../services/saleOrderService';
import { generateAndPreviewSaleOrderPDF } from '../services/saleOrderPdfService';
import { useStoreInfo } from '../../../hooks/useCompanyInfo';
import html2pdf from 'html2pdf.js';
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import { PAYMENT_STATUS } from '../../../constants/paymentStatus';

// Icons
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

// Components
import SaleOrderStatusChip from '../components/SaleOrderStatusChip';
import PaymentStatusChip from '../components/PaymentStatusChip';

const ListSaleOrderPage = () => {
  const [saleOrders, setSaleOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSaleOrder, setSelectedSaleOrder] = useState(null);
  const { t } = useTranslation();
  const { storeInfo } = useStoreInfo();
  const language = useSelector((state) => state.settings?.language) || 'en';
  
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchSaleOrders();
  }, []);

  const fetchSaleOrders = async () => {
    setLoading(true);
    try {
      const response = await saleOrderService.getSaleOrders();
      // Handle paginated response structure: response.payload.Items
      const saleOrdersArray = response?.payload?.Items || response?.payload || response?.data || [];
      setSaleOrders(Array.isArray(saleOrdersArray) ? saleOrdersArray : []);
    } catch (error) {
      console.error('Error fetching sale orders:', error);
      toast.error(t('failed_to_load_sale_orders'));
      setSaleOrders([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchSaleOrders();
      toast.success(t('sale_orders_refreshed'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteClick = (saleOrder) => {
    setSelectedSaleOrder(saleOrder);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSaleOrder) return;
    
    try {
      await saleOrderService.deleteSaleOrder(selectedSaleOrder.Id);
      toast.success(t('sale_order_deleted'));
      fetchSaleOrders();
    } catch (error) {
      console.error('Error deleting sale order:', error);
      toast.error(t('failed_to_delete_sale_order'));
    } finally {
      setDeleteDialogOpen(false);
      setSelectedSaleOrder(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : language === 'fr' ? 'fr-FR' : 'en-GB');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'nl' ? 'nl-NL' : language === 'fr' ? 'fr-FR' : 'en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  const handlePreviewPdf = async (saleOrder) => {
    setPreviewLoading(true);
    try {
      const fullSO = await saleOrderService.getSaleOrderById(saleOrder.Id);
      const data = fullSO?.payload || fullSO?.data || fullSO || saleOrder;
      
      await generateAndPreviewSaleOrderPDF(data, storeInfo, language, t);
      
      toast.success(t('pdf_generated'));
    } catch (error) {
      console.error('Error generating sale order PDF:', error);
      toast.error(t('failed_to_generate_pdf'));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Filter sale orders based on search query
  const filteredSaleOrders = saleOrders.filter((so) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      (so.SaleOrderNumber?.toLowerCase().includes(query)) ||
      (so.CustomerName?.toLowerCase().includes(query)) ||
      (so.Customer?.CompanyName?.toLowerCase().includes(query)) ||
      (so.Customer?.Name?.toLowerCase().includes(query)) ||
      (so.Status?.toLowerCase().includes(query)) ||
      (so.PaymentStatus?.toLowerCase().includes(query))
    );
  });

  // Stats calculation
  const stats = {
    total: saleOrders.length,
    draft: saleOrders.filter(so => so.Status?.toLowerCase() === 'draft').length,
    closed: saleOrders.filter(so => so.Status?.toLowerCase() === 'closed').length,
    totalValue: saleOrders.reduce((sum, so) => sum + (Number(so.TotalIncl) || 0), 0),
  };

  const columns = [
    {
      field: 'SaleOrderNumber',
      headerName: t('sale_order_number'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCartIcon fontSize="small" color="primary" />
          <Typography variant="body3" fontWeight={600}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'CustomerName',
      headerName: t('customer'),
      flex: 1.2,
      minWidth: 200,
      renderCell: (params) => params.row.Customer?.CompanyName || params.row.Customer?.Name || params.value || t('unknown_customer'),
    },
    {
      field: 'SaleOrderDate',
      headerName: t('date'),
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => formatDate(params.value || params.row.CreatedDate),
    },
    {
      field: 'Status',
      headerName: t('status'),
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => <SaleOrderStatusChip status={params.value} />,
    },
    {
      field: 'PaymentStatus',
      headerName: t('payment_status'),
      flex: 0.9,
      minWidth: 140,
      renderCell: (params) => <PaymentStatusChip paymentStatus={params.value || PAYMENT_STATUS.NOT_PAID} />,
    },
    {
      field: 'TotalIncl',
      headerName: t('total'),
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body3" fontWeight={600}>
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1.2,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => {
        const isDraft = params.row.Status?.toLowerCase() === 'draft';
        
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={t('view_details')}>
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/sales/sale-orders/${params.row.Id}`);
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {/* Continue button - only for Draft orders */}
            {isDraft && (
              <Tooltip title={t('continue')}>
                <IconButton
                  size="small"
                  color="info"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/sales/sale-orders/add?id=${params.row.Id}`);
                  }}
                >
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            
            <Tooltip title={t('preview_pdf')}>
              <IconButton
                size="small"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreviewPdf(params.row);
                }}
              >
                <PictureAsPdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {/* Delete button - only for Draft orders */}
            {isDraft && (
              <Tooltip title={t('delete')}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(params.row);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 3, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ShoppingCartIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {t('sale_orders')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {t('manage_sale_orders')}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddCircleIcon />}
            onClick={() => navigate('/sales/sale-orders/add')}
            sx={{
              bgcolor: 'white',
              color: '#667eea',
              fontWeight: 700,
              '&:hover': {
                bgcolor: '#f5f5f5',
              },
            }}
          >
            {t('create_sale_order')}
          </Button>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t('total_sale_orders')}
              </Typography>
              <Typography variant="h4" fontWeight={700}>
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t('draft_orders')}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {stats.draft}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t('closed_orders')}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="info.main">
                {stats.closed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                {t('total_value')}
              </Typography>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {formatCurrency(stats.totalValue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Bar */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder={t('search_sale_orders_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1, minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={t('refresh')}>
            <IconButton 
              onClick={handleRefresh} 
              disabled={refreshing}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Data Grid */}
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <ReusableDataGrid
            rows={filteredSaleOrders}
            columns={columns}
            getRowId={(row) => row.Id}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            loading={loading}
            initialState={{
              sorting: {
                sortModel: [{ field: 'SaleOrderDate', sort: 'desc' }],
              },
            }}
          />
        )}
      </Paper>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={previewLoading || refreshing}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        title={t('confirm_delete')}
        message={t('confirm_delete_sale_order_message', { number: selectedSaleOrder?.SaleOrderNumber })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedSaleOrder(null);
        }}
        confirmText={t('delete')}
        cancelText={t('cancel')}
        variant="danger"
      />
    </Container>
  );
};

export default ListSaleOrderPage;
